/**
 * StickySaveBar — barra pegajosa de salvamento agregado (03c, Task 5).
 *
 * Lê as seções "sujas" registradas em `DomainSaveProvider` (uma árvore por
 * `DomainPage`) e chama `save()`/`discard()`/`undo()` de CADA UMA — funções
 * que a própria seção definiu e que usam a mutation EXISTENTE dela. Esta
 * barra não importa nenhum hook de mutation, endpoint ou query key (Global
 * Constraint do plano: "1 caminho de escrita por dado" — os hooks de
 * mutation existentes continuam sendo o ÚNICO caminho de escrita, a barra só
 * agrega apresentação). Ações de risco (aplicar perfil, wizard de churn) não
 * se registram aqui — mantêm botão + confirmação vinculante próprios; nada
 * neste arquivo dá a elas um caminho para a barra.
 *
 * Vocabulário reaproveitado de `PersonaActionBar.tsx` (mesma família de
 * conceito — barra de ações quando há alteração não salva — mecânica
 * diferente): "Alterações não salvas" / "Salvando…" / "Descartar" / "Salvar".
 * O componente NÃO é reusado (seria abstração prematura: PersonaActionBar
 * liga 1 formulário a 1 mutation; esta barra agrega um REGISTRO dinâmico de
 * N seções, nomeia os campos alterados de todas e oferece desfazer de
 * sessão).
 *
 * Fidelidade — prancha "03 Item de config, busca e salvar.dc.html", opção
 * 3c. Duas divergências DECLARADAS do texto do plano, resolvidas a favor da
 * prancha (fidelidade tem precedência quando os dois discordam):
 *   1. Separador do texto da barra: a prancha usa travessão ("2 alterações
 *      não salvas — postura de escalada, checklist"), o texto do plano
 *      sugeria dois-pontos. Sem custo de dado — a prancha venceu.
 *   2. O toast da prancha mostra "campo → novo valor" por alteração (ex.:
 *      "Postura de escalada → moderada · checklist desligado"). O contrato
 *      `SectionSaveRegistration` só carrega `changedLabels: readonly
 *      string[]` — nomes de campo, sem valor antigo/novo. Não há dado para
 *      reproduzir esse formato; o toast aqui nomeia só os campos alterados
 *      ("Salvo: <labels>"). Ver task-5-report.md para o racional completo.
 *
 * Fix round 1 (Finding 1, CRITICAL): `handleSave`/`handleDiscard` executam
 * sobre `getLiveSections()` (leitura ao vivo, sem memoização) — NUNCA sobre
 * `sections` (o array retornado por `useSaveBarSections`, que é um snapshot
 * memoizado só para exibição). Ver o docblock de `save-bar-context.tsx` para
 * o porquê: reregistrar com o MESMO `changedLabels` mas um `save` diferente
 * (ex.: 2ª tecla no mesmo campo) não invalida o snapshot de exibição, e um
 * consumidor que executasse a partir dele chamaria o closure velho.
 */
import { useState } from 'react';

import { useToastStore } from '../../../stores/toast-store';

import { useSaveBarLiveSections, useSaveBarSections } from './save-bar-context';
import './hub.css';

import type { SectionSaveRegistration } from './save-bar-context';

const TOAST_DURATION_MS = 4000;

function flattenLabels(sections: readonly SectionSaveRegistration[]): readonly string[] {
  return sections.flatMap((s) => s.changedLabels);
}

function formatLabelList(labels: readonly string[]): string {
  return labels.join(', ');
}

/**
 * Fix round 1 (Finding 4): quando nenhuma label chegou (registro com
 * `changedLabels: []`), a barra não deve afirmar "0 alterações" enquanto
 * ainda está visível — cai no vocabulário genérico de `PersonaActionBar`.
 */
function formatContagem(total: number): string {
  if (total === 0) return 'Alterações não salvas';
  return total === 1 ? '1 alteração não salva' : `${total} alterações não salvas`;
}

export function StickySaveBar(): JSX.Element | null {
  // Snapshot memoizado — só para RENDERIZAR (contagem/labels). Está sempre
  // correto pra isso: qualquer mudança visível de label invalida o snapshot.
  const sections = useSaveBarSections();
  // Leitura ao vivo — só para EXECUTAR (Finding 1). Nunca usar `sections`
  // acima dentro de `handleSave`/`handleDiscard`.
  const getLiveSections = useSaveBarLiveSections();
  const [isSaving, setIsSaving] = useState(false);
  const push = useToastStore((s) => s.push);

  if (sections.length === 0) return null;

  const changedLabels = flattenLabels(sections);

  async function handleSave(): Promise<void> {
    // Leitura AO VIVO no momento do clique — nunca a `sections` memoizada
    // pra exibição, que pode apontar pra um closure velho (Finding 1). Isso
    // também garante que uma seção já salva (e portanto já não mais
    // registrada, porque a própria seção parou de se dar como suja) nunca é
    // re-salva num clique de retry: ela simplesmente não está mais na lista.
    const batch = getLiveSections();
    setIsSaving(true);
    try {
      const results = await Promise.allSettled(batch.map((s) => s.save()));

      const succeeded: SectionSaveRegistration[] = [];
      const failed: SectionSaveRegistration[] = [];
      results.forEach((result, i) => {
        const section = batch[i];
        if (section === undefined) return;
        if (result.status === 'fulfilled') {
          succeeded.push(section);
        } else {
          failed.push(section);
        }
      });

      // Uma rejeição não aborta as outras (Promise.allSettled, não
      // Promise.all) — cada seção teve sua chance independentemente.
      if (succeeded.length > 0) {
        const savedLabels = formatLabelList(flattenLabels(succeeded));
        const allHaveUndo = succeeded.every((s) => s.undo !== undefined);
        push({
          variant: 'success',
          title: `Salvo: ${savedLabels}`,
          durationMs: TOAST_DURATION_MS,
          ...(allHaveUndo && {
            actionLabel: 'Desfazer',
            onAction: () => {
              // Desfazer da sessão: chama o `undo` de cada seção salva, que
              // reaplica os valores anteriores pela MESMA mutation dela.
              void Promise.allSettled(
                succeeded.map((s) => (s.undo === undefined ? Promise.resolve() : s.undo())),
              );
            },
          }),
        });
      }

      if (failed.length > 0) {
        const failedLabels = formatLabelList(flattenLabels(failed));
        push({
          variant: 'error',
          title: `Falha ao salvar: ${failedLabels}`,
          durationMs: TOAST_DURATION_MS,
        });
      }
    } finally {
      setIsSaving(false);
    }
  }

  function handleDiscard(): void {
    // Idem: leitura ao vivo, não o snapshot de exibição (Finding 1).
    getLiveSections().forEach((s) => s.discard());
  }

  return (
    <div
      role="region"
      aria-label="Alterações não salvas"
      className="hub-savebar sticky bottom-0 z-10 mt-6"
    >
      <span aria-hidden className="hub-savebar__dot" />
      <span className="hub-savebar__text">
        {formatContagem(changedLabels.length)}
        {changedLabels.length > 0 && (
          <>
            {' — '}
            <span className="hub-savebar__muted">{formatLabelList(changedLabels)}</span>
          </>
        )}
      </span>
      <button
        type="button"
        disabled={isSaving}
        onClick={handleDiscard}
        className="hub-savebar__btn hub-savebar__btn--ghost"
      >
        Descartar
      </button>
      <button
        type="button"
        disabled={isSaving}
        onClick={() => void handleSave()}
        className="hub-savebar__btn hub-savebar__btn--primary"
      >
        {isSaving ? 'Salvando…' : 'Salvar alterações'}
      </button>
    </div>
  );
}
